import debounce from "@/utils/debounce";
import { defineStore } from "@/utils/define-component";
import { CartData, ProductData } from "@/utils/types";

type ID = number;
type Quantity = number;
type UpdateCartParams = Record<ID, Quantity>;

const dispatchUpdateCart = debounce<UpdateCartParams, CartData | undefined>(
  async (data, { signal }) => {
    window.dispatchEvent(
      new CustomEvent("is-loading", {
        detail: { id: "cart", isLoading: true },
      }),
    );

    try {
      const res = await fetch("/cart/update.js", {
        signal,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          updates: data,
        }),
      });

      const json = (await res.json()) as CartData;

      window.dispatchEvent(
        new CustomEvent("is-loading", {
          detail: { id: "cart", isLoading: false },
        }),
      );

      return json;
    } catch (error) {
      window.dispatchEvent(
        new CustomEvent("is-loading", {
          detail: { id: "cart", isLoading: false },
        }),
      );
    }
  },
);

export default defineStore({
  cartData: null as CartData | null,
  allProductsData: null as ProductData[] | null,

  cartItemCount: 0 as number,

  getProductRequires(product) {
    if (!this.allProductsData || !product?.tags.length) return [];

    const requiredTag = product.tags
      .filter((tag) => tag.split("requires:")[1])
      .map((tag) => tag?.split("requires:")[1]);

    return this.allProductsData.filter((maybeRequiredProduct) => {
      if (!maybeRequiredProduct.tags.length) return;

      return maybeRequiredProduct.tags.find((tag) => requiredTag.includes(tag));
    });
  },

  getFreeGiftsRelatedToProduct(product): ProductData[] {
    if (!this.allProductsData || !product?.tags.length) return [];

    return this.allProductsData.filter((maybeFreeProduct) => {
      return maybeFreeProduct.tags.find((tag) => {
        const targetTag = tag.split("follows:")[1];

        return targetTag && product.tags.includes(targetTag);
      });
    });
  },

  productFromId(variantId: number): ProductData {
    if (!this.allProductsData) return;

    return this.allProductsData.find((product) => {
      return product.variants.find((variant) => {
        return variant.id === variantId;
      });
    });
  },

  parseCartData(): UpdateCartParams {
    let data = {};

    this.cartData?.items.forEach((item) => {
      data[item.id] = item.quantity;
    });

    return data;
  },

  encodeCartUpdateData({ id, quantity }: { id: number; quantity: number }) {
    const currentCart = this.parseCartData();

    const updatedCart = {
      ...currentCart,
      [id]: quantity,
    };

    for (let variantId in updatedCart) {
      const currentProduct = this.productFromId(parseInt(variantId));
      const quantity = updatedCart[variantId];

      /**
       * add gift products,
       * checks is any product not in the cart should be added based on the current cart item
       */
      const gifts = this.getFreeGiftsRelatedToProduct(currentProduct);

      if (gifts.length) {
        gifts.forEach((gift) => {
          const [firstVariant] = gift.variants;

          if (quantity > 0) {
            updatedCart[firstVariant.id] = 1;
          }
        });
      }

      /**
       * remove gift products,
       * checks if the current item is a gift item, and if it has any product that gifts it
       */
      if (currentProduct?.tags.find((tag) => tag.includes("follows:"))) {
        const giftTags = currentProduct.tags
          .filter((tag) => tag.includes("follows:"))
          .map((tag) => tag.split("follows:")[1]);

        const giftCanBeGiven = giftTags.find((tag) => {
          for (let otherId in updatedCart) {
            const otherProduct = this.productFromId(parseInt(otherId));

            const isGifting = otherProduct.tags.includes(tag);
            const isInCart = updatedCart[otherId] > 0;

            return isGifting && isInCart;
          }
        });

        if (!giftCanBeGiven) {
          updatedCart[variantId] = 0;
        }
      }

      /**
       * remove products that requires another product that's not in the cart
       */
      const requiredProducts = this.getProductRequires(currentProduct);

      if (requiredProducts.length) {
        const variantIdsRequired = requiredProducts
          .map((requiredProduct) =>
            requiredProduct.variants.map((variant) => variant.id),
          )
          .flat();

        const requiredProductInCart = variantIdsRequired.find(
          (requiredVariantId) => {
            return updatedCart[requiredVariantId];
          },
        );

        if (!requiredProductInCart) {
          updatedCart[variantId] = 0;
        }
      }
    }

    return updatedCart;
  },

  async updateCart({ id, quantity }) {
    const quantityOfItemInCart =
      this.cartData.items.find((item) => item.id === id)?.quantity || 0;

    const updateCartData = this.encodeCartUpdateData({
      id: parseInt(id),
      quantity: quantityOfItemInCart + parseInt(quantity),
    });

    const newCart = await dispatchUpdateCart(updateCartData);

    // update was debounced
    if (!newCart) return;

    this.cartData = newCart;

    this.updateItemCount();

    window.dispatchEvent(new CustomEvent("cart-updated"));
  },

  async updateItemCount() {
    try {
      const response = await fetch("/cart.json");
      const json = await response.json();
      this.cartItemCount = json.item_count;
    } catch (error) {
      return null;
    }
  },

  setCartItemAmount(amount: number) {
    this.cartItemCount = amount;
  },

  async init() {
    if (!this.allProductsData) {
      const res = await fetch("/products.json");
      const json = await res.json();
      this.allProductsData = json?.products || [];
    }

    if (!this.cartData) {
      const res = await fetch("/cart.json");
      const json = await res.json();
      this.cartData = json || null;
      this.cartItemCount = json.item_count;
    }

    window.addEventListener(
      "update-cart",
      (e: CustomEventInit<{ id: number; quantity: number }>) => {
        this.updateCart(e.detail);
      },
    );
  },
});
